import { WSMessage, WSMessageType, createWSMessage } from '../types/websocket';

export enum MessageStatus {
  PENDING = 'pending',
  SENT = 'sent',
  DELIVERED = 'delivered',
  FAILED = 'failed'
}

export interface QueuedMessage {
  id: string;
  message: WSMessage;
  status: MessageStatus;
  retryCount: number;
  maxRetries: number;
  timestamp: number;
  lastRetryTime?: number;
}

interface MessageQueueConfig {
  maxRetries?: number;
  retryDelay?: number;
  storageKey?: string;
}

export class MessageQueue {
  /** 队列条数上限：超过时丢弃最旧消息，防止 localStorage 无界增长（05-5.7） */
  private static readonly MAX_QUEUE_SIZE = 200;
  /** 消息过期时间：24h，load 时过滤 */
  private static readonly MESSAGE_TTL_MS = 24 * 60 * 60 * 1000;
  private queue: QueuedMessage[] = [];
  private isProcessing = false;
  private maxRetries: number;
  private retryDelay: number;
  private storageKey: string;
  private sendMessageCallback: ((message: WSMessage) => Promise<void>) | null = null;
  private statusUpdateCallback: ((messageId: string, status: MessageStatus) => void) | null = null;

  constructor(config: MessageQueueConfig = {}) {
    this.maxRetries = config.maxRetries ?? 3;
    this.retryDelay = config.retryDelay ?? 2000;
    this.storageKey = config.storageKey ?? 'message-queue';
    this.loadFromStorage();
  }

  setSendMessageCallback(callback: (message: WSMessage) => Promise<void>) {
    this.sendMessageCallback = callback;
  }

  setStatusUpdateCallback(callback: (messageId: string, status: MessageStatus) => void) {
    this.statusUpdateCallback = callback;
  }

  enqueue(message: WSMessage): string {
    const queuedMessage: QueuedMessage = {
      id: this.generateMessageId(),
      message,
      status: MessageStatus.PENDING,
      retryCount: 0,
      maxRetries: this.maxRetries,
      timestamp: Date.now()
    };

    this.queue.push(queuedMessage);

    // 超过上限丢弃最旧消息，避免长期失联后恢复时的"迟到旧消息"突袭与存储无界增长
    while (this.queue.length > MessageQueue.MAX_QUEUE_SIZE) {
      const dropped = this.queue.shift();
      console.warn('MessageQueue: dropped oldest queued message due to size limit', dropped?.id);
    }

    this.saveToStorage();
    this.process();

    return queuedMessage.id;
  }

  private async process() {
    if (this.isProcessing || this.queue.length === 0 || !this.sendMessageCallback) {
      return;
    }

    this.isProcessing = true;

    const pendingMessages = this.queue.filter(
      msg => msg.status === MessageStatus.PENDING
    );

    for (const queuedMessage of pendingMessages) {
      const now = Date.now();
      
      if (queuedMessage.lastRetryTime && 
          now - queuedMessage.lastRetryTime < this.retryDelay) {
        continue;
      }

      try {
        await this.sendMessageCallback(queuedMessage.message);
        
        this.updateMessageStatus(queuedMessage.id, MessageStatus.SENT);
        this.removeFromQueue(queuedMessage.id);
        
      } catch (error) {
        console.error(`Failed to send message ${queuedMessage.id}:`, error);
        
        queuedMessage.retryCount++;
        queuedMessage.lastRetryTime = now;
        
        if (queuedMessage.retryCount >= queuedMessage.maxRetries) {
          this.updateMessageStatus(queuedMessage.id, MessageStatus.FAILED);
        } else {
          this.saveToStorage();
        }
      }
    }

    this.isProcessing = false;

    if (this.queue.some(msg => msg.status === MessageStatus.PENDING)) {
      setTimeout(() => this.process(), this.retryDelay);
    }
  }

  private updateMessageStatus(messageId: string, status: MessageStatus) {
    const message = this.queue.find(msg => msg.id === messageId);
    if (message) {
      message.status = status;
      this.saveToStorage();
      
      if (this.statusUpdateCallback) {
        this.statusUpdateCallback(messageId, status);
      }
    }
  }

  private removeFromQueue(messageId: string) {
    this.queue = this.queue.filter(msg => msg.id !== messageId);
    this.saveToStorage();
  }

  retry(messageId: string): boolean {
    const message = this.queue.find(msg => msg.id === messageId);
    if (message && message.status === MessageStatus.FAILED) {
      message.status = MessageStatus.PENDING;
      message.retryCount = 0;
      this.saveToStorage();
      this.process();
      return true;
    }
    return false;
  }

  clear(): void {
    this.queue = [];
    this.saveToStorage();
  }

  getQueue(): QueuedMessage[] {
    return [...this.queue];
  }

  getPendingCount(): number {
    return this.queue.filter(msg => msg.status === MessageStatus.PENDING).length;
  }

  getFailedCount(): number {
    return this.queue.filter(msg => msg.status === MessageStatus.FAILED).length;
  }

  private saveToStorage(): void {
    try {
      const data = JSON.stringify(this.queue);
      localStorage.setItem(this.storageKey, data);
    } catch (error) {
      console.error('Failed to save message queue to storage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (data) {
        const parsed = JSON.parse(data) as QueuedMessage[];
        this.queue = parsed.filter(msg => {
          const isExpired = Date.now() - msg.timestamp > MessageQueue.MESSAGE_TTL_MS;
          return !isExpired && msg.status !== MessageStatus.FAILED;
        });
        this.saveToStorage();
      }
    } catch (error) {
      console.error('Failed to load message queue from storage:', error);
    }
  }

  private generateMessageId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

let globalMessageQueue: MessageQueue | null = null;

export const getGlobalMessageQueue = (): MessageQueue => {
  if (!globalMessageQueue) {
    globalMessageQueue = new MessageQueue({
      maxRetries: 3,
      retryDelay: 2000,
      storageKey: 'message-queue'
    });
  }
  return globalMessageQueue;
};